// AddContentController.js

import axios from 'axios';
import qs from 'qs';  // Import qs for proper URL encoding
import { sessionId } from '../../App';
import { ArtworkTypes } from '../../components/constants/ArtworkTypes';

const baseURL = "https://wallmuse.com:8443/wallmuse/ws";

// 1.2. Utility function to convert datation text to start and end dates in the format YYYY0101
export function convertDatationText(datationText) {
    let datationStart = '';
    let datationEnd = '';

    if (datationText) {
        const years = datationText.split('-');

        if (years.length === 2) {
            // Handle year range, e.g., "1990-2000"
            datationStart = `${years[0]}0101`;
            datationEnd = `${years[1]}0101`;
        } else if (years.length === 1 && years[0]) {
            // Handle single year, e.g., "1990"
            datationStart = `${years[0]}0101`;
            datationEnd = `${years[0]}0101`;
        }
    }

    return { datationStart, datationEnd };
}

// 3. Function to handle the file upload process
export async function handleUpload(contentData, contentId, selectedFiles) {
    console.log("[handleUpload] contentData before saving artwork:", contentData);
    console.log('HD file size:', selectedFiles.hdFile?.size);
    try {
        let artworkId = contentId;

        // Step 1: If no contentId, save artwork metadata to get an artworkId
        if (!artworkId) {
            console.log("[api handleUpload] No contentId found. Saving new artwork data...");
            artworkId = await saveInitialArtwork(contentData, selectedFiles);
            console.log("[api handleUpload] New artworkId created:", artworkId);

            if (!artworkId) {
                throw new Error("Failed to create new artwork.");
            }
        }

        // Step 2: Upload media files (HD, SD, Thumbnail)
        const uploadPromises = [];

        if (selectedFiles.hdFile) {
            console.log("[handleUpload] Uploading HD file...");
            uploadPromises.push(uploadAdditionalFiles(selectedFiles.hdFile, 'HD', artworkId));
        }

        if (selectedFiles.sdFile) {
            console.log("[handleUpload] Uploading SD file...");
            uploadPromises.push(uploadAdditionalFiles(selectedFiles.sdFile, 'SD', artworkId));
        }

        if (selectedFiles.thumbnailFile) {
            console.log("[handleUpload] Uploading Thumbnail file...");
            uploadPromises.push(uploadAdditionalFiles(selectedFiles.thumbnailFile, 'THUMBNAIL', artworkId));
        }

        const uploadResponses = await Promise.all(uploadPromises);
        console.log("[handleUpload] File upload completed:", uploadResponses);

    } catch (error) {
        console.error("[handleUpload] Error during file upload operation:", error);
        alert('An error occurred during the upload process.');
    }
}

export const isValidDatation = (datationText) => {
    return /^[0-9]{4}(-[0-9]{4})?$/.test(datationText);
};

export async function saveInitialArtwork(contentData) {
    // Create the XML structure
    const xmlDoc = document.implementation.createDocument('', '', null);
    const artwork = xmlDoc.createElement('artwork');

    // Set attributes to the <artwork> element
    if (contentData.artworkId) {
        artwork.setAttribute('id', contentData.artworkId);
    }
    // console.log ('[AddContentController] contentData.versionType check again', contentData.versionType );
    console.log("[AddContent] Categories to be submitted:", contentData.categories);  // Should be an array of IDs like [80, 275]
    console.log("[AddContentController] Keywords before adding to XML:", contentData.keywords);

    // artwork.setAttribute('type', contentData.versionType || ArtworkTypes.VIDEO); // Defaults to 'VID' if not provided
    artwork.setAttribute('name', contentData.title); // controller le titre

    contentData.author_name = contentData.authors && Array.isArray(contentData.authors) 
    ? contentData.authors.map(author => author.display_name).join(', ') 
    : 'Unknown Author'; // or another fallback

    artwork.setAttribute('public_sd', 'Y');
    artwork.setAttribute('streaming', contentData.streaming ? 'Y' : 'N');
    artwork.setAttribute('splittable', contentData.splittable ? 'Y' : 'N');
    artwork.setAttribute('croppable', contentData.croppable ? 'Y' : 'N');
    artwork.setAttribute('deconstructable', contentData.deconstructable ? 'Y' : 'N');
    artwork.setAttribute('keywords', contentData.keywords ? contentData.keywords.join(',') : '');
    artwork.setAttribute('datation_start', contentData.datationStart || '');
    artwork.setAttribute('datation_end', contentData.datationEnd || '');
    artwork.setAttribute('datation_hidden', contentData.datationHidden ? 'Y' : 'N'); // $$$ returns true
    artwork.setAttribute('datation_kind', contentData.datationKind ? 'Y' : 'N'); // $$$ returns false, except if a state that required render to update it's value???
    artwork.setAttribute('author_name', contentData.artworkAuthor || 'Unknown Author');

    // Add categories to XML
    if (Array.isArray(contentData.categories)) {
        contentData.categories.forEach(categoryId => {
            const categoryElement = xmlDoc.createElement("category");
            categoryElement.setAttribute("id", categoryId);
            artwork.appendChild(categoryElement);
        });
    }

    // 1.2. Handle descriptions as repeated elements
    // Assuming contentData.descriptions exists and contains valid description data
    if (Array.isArray(contentData.descriptions)) {
        contentData.descriptions.forEach((desc, index) => {
            if (desc && desc.language && desc.description) {
                // Log the description to make sure it exists
                console.log(`[AddContentController] Adding description #${index}:`, desc);

                // Create the <description> element with attributes
                const descriptionElement = xmlDoc.createElement('description');
                descriptionElement.setAttribute('language', desc.language);
                descriptionElement.setAttribute('description', desc.description);

                // Append the description element to the artwork
                artwork.appendChild(descriptionElement);
            }
        });
    }

    // 3. Append URLs (if they exist)
    if (contentData.hdPath) {
        const url = xmlDoc.createElement('url');
        url.setAttribute('url', contentData.hdPath);
        url.setAttribute('kind', 'HD');
        artwork.appendChild(url);
    }
    if (contentData.sdPath) {
        const url = xmlDoc.createElement('url');
        url.setAttribute('url', contentData.sdPath);

        url.setAttribute('kind', 'SD');
        artwork.appendChild(url);
    }
    if (contentData.thumbPath) {
        const url = xmlDoc.createElement('url');
        url.setAttribute('url', contentData.thumbPath);
        url.setAttribute('kind', 'THUMBNAIL');
        artwork.appendChild(url);
    }

    // 4. Handle rights (as copyright elements)
    const directionType = 'A';  //Always, M maybe for Montages elsewhere or something else

    if (Array.isArray(contentData.rights)) {

        contentData.rights.forEach((right, index) => {
            // Use author_name as the owner if present, otherwise fallback
            const ownerName = right.owner?.name || contentData.author_name || 
                              (Array.isArray(contentData.authors) 
                              ? contentData.authors.map(author => author.display_name).join(', ') 
                              : 'Fallback Author');

            if (right && right.type && right.country && ownerName) {
                const copyrightElement = xmlDoc.createElement('copyright');
                copyrightElement.setAttribute('type', right.type);
                copyrightElement.setAttribute('country', right.country || 'ALL');  
                copyrightElement.setAttribute('owner', ownerName);
                copyrightElement.setAttribute('direction', directionType);
                artwork.appendChild(copyrightElement);
            } else {
                console.warn(`[AddContentController] Invalid right at index #${index}. Missing fields:`, {
                    type: right.type,
                    country: right.country,
                    owner: ownerName
                });
            }
        });
    }

    // 5. Add credits to the XML
    if (Array.isArray(contentData.credits)) {
        contentData.credits.forEach(credit => {
            if (credit.name && credit.type) { // Ensure name and type are provided
                const creditElement = xmlDoc.createElement("credit");
                creditElement.setAttribute("name", credit.name);
                console.log("Credit name:", credit.name);
                creditElement.setAttribute("type", credit.type);
                creditElement.setAttribute("location", credit.location || 'ALL');
                artwork.appendChild(creditElement);
            } else {
                console.warn('Skipping credit due to missing name or type.');
            }
        });
    }

    // Finalize the XML
    xmlDoc.appendChild(artwork);
    const artworkXML = new XMLSerializer().serializeToString(xmlDoc);

    // Prepare the POST data as per `set_artwork_full` requirements
    const formData = qs.stringify({
        version: '1',                 // Version parameter
        session: sessionId,            // Session ID
        artwork: artworkXML            // The constructed XML
    });

    // Send the request to the `set_artwork_full` endpoint
    try {
        const response = await axios.post(`${baseURL}/set_artwork_full`, formData, {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            }
        });
        console.log('[api set_artwork_full]', response.data); 
        // The current implementation of datationHidden and datationKind works as expected in the final payload.
        // Although there was an initial consideration to switch entirely to useRef, the current approach, 
        // using a combination of useState (for managing reactivity in the UI) and useRef (to hold persistent 
        // values without triggering re-renders), has proven to be effective.
        // This solution avoids unnecessary re-renders while maintaining synchronization between the UI and 
        // the final payload. For now, no further changes are needed since this achieves the desired behavior.

        // Handle response (assuming it's in XML format)
        const parser = new DOMParser();
        const xmlDocResponse = parser.parseFromString(response.data, 'text/xml');
        const artworkId = xmlDocResponse.getElementsByTagName('artwork')[0]?.getAttribute('id');

        if (!artworkId) {
            throw new Error('No artworkId found in the response');
        }

        return artworkId;
    } catch (error) {
        console.error('[saveInitialArtwork] Error:', error);
        throw error;
    }
}

// Upload additional files (SD, Thumbnail) using the artworkId from the HD upload
export async function uploadAdditionalFiles(file, kind, artworkId) {
    const formData = new FormData();
    formData.append('version', '1');
    formData.append('session', sessionId);
    formData.append('artwork', artworkId);
    formData.append('kind', kind);
    formData.append('file', file);

    try {
        const response = await axios.post(`${baseURL}/upload_file`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });

        if (response.status === 200) {
            const responseData = response.data;

            // Parse the XML response
            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(responseData, 'application/xml');

            // Extract the URLs from the XML
            const urlElements = xmlDoc.getElementsByTagName('url');

            // Loop through URLs to find the correct one by `kind`
            let uploadedUrl = null;
            for (let i = 0; i < urlElements.length; i++) {
                if (urlElements[i].getAttribute('kind') === kind) {
                    uploadedUrl = urlElements[i].getAttribute('url');
                    break;
                }
            }

            if (uploadedUrl) {
                console.log(`[uploadAdditionalFiles] ${kind} file uploaded successfully:`, uploadedUrl);
                return uploadedUrl;
            } else {
                throw new Error(`No ${kind} URL found in the response.`);
            }
        } else {
            throw new Error(`Failed to upload ${kind} file.`);
        }
    } catch (error) {
        console.error(`[uploadAdditionalFiles] Error uploading ${kind} file:`, error);
        throw error;
    }
}
